
	import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
	import RightFootSVG from '@/components/RightFootSVG.vue';
	import LeftFootSVG from '@/components/LeftFootSVG.vue';
	import Fitstation3DScan from '@/components/Fitstation3DScan/Fitstation3DScan.vue';
	import Loading from '@/components/Loading.vue';
	import { reaction } from 'mobx';
	import errorMessages from '@/assets/error-messages.json';
	import IQubeSVGWithFoot from '@/components/modals/IQubeSVGWithFoot.vue';
	import { Observer } from 'mobx-vue';
	import { optionalUpdateData } from '@/store/engine/modules/ProfileModule';

	@Observer
	@Component({
		components: { IQubeSVGWithFoot, RightFootSVG, LeftFootSVG, Fitstation3DScan, Loading }
	})
	export default class FootScanner extends Vue {
		@Prop() scanType!: 'leftscan' | 'rightscan';
		@Prop() buttonDisabled!: boolean;

		ScanState = ScanState;
		public scanState: ScanState = ScanState.CHECKING;

		@Watch('scanState')
		onStateChanged(val: any, oldVal: any) {
			this.$emit('state', val);
		}

		private activated(): void {
			if (this.scanState === ScanState.SCANNING_ERROR) {
				this.scanState = ScanState.CHECKING;
				this.checkIfScanExist();
			}
		}

		private created(): void {
			window.addEventListener('requestForRescan', () => {
				console.log('HERE 44444');
				this.HPAgentstartScan(true);
			});
			window.addEventListener('message', this.agentMessageHandler.bind(this), false);
			window.addEventListener('OnScanUploadCompleted', async (e) => {
				this.$emit('updateSession');
				if (this.scanState === ScanState.SCANNING) {
					this.scanState = ScanState.RENDERING;
				}
			});
			window.addEventListener('OnScanFailed', async (e) => {
				if (this.scanState === ScanState.SCANNING) {
					this.scanState = ScanState.SCANNING_ERROR;
					this.$emit('scanError', 'Scan Failed');
				}
			});
			window.addEventListener('OnScanUploadFailed', async (e) => {
				if (this.scanState === ScanState.SCANNING || this.scanState === ScanState.RENDERING) {
					this.scanState = ScanState.SCANNING_ERROR;
					this.$emit('scanError', 'Upload Failed');
				}
			});

			reaction(
				() => this.$state.biometric[(this.scanType.includes('left') ? 'left' : 'right') + 'ScanExists'],
				(val) => {
					if (val === false && this.scanState === ScanState.RENDERED) {
						this.scanState = ScanState.NOT_FOUND;
					}
				}
			);
		}

		public async checkIfScanExist() {
			const scans = await this.$state.biometric.getLatestScans();

			const scanURL = this.scanType === 'leftscan' ? scans.left.blobURL : scans.right.blobURL;
			if (scanURL) {
				this.scanState = ScanState.RENDERING;
				return;
			}

			this.scanState = ScanState.NOT_FOUND;
		}

		private async mounted(): Promise<void> {
			await this.checkIfScanExist();
		}

		private scanTypeToTitle() {
			return this.scanType === 'leftscan' ? 'Left' : 'Right';
		}

		private async HPAgentstartScan(rescan = false) {
			this.$state.agent.scannedInThisSession = true;

			this.$emit('clicked');
			this.scanState = ScanState.SCANNING;

			try {
				const agent = this.$state.engine.agent;
				// make sure scans are not dirty
				await this.$state.biometric.getLatestScans();
				const hadScansBefore = this.$state.biometric.oneScanExists;

				if (this.$state._patient.currentSessionID) {
					await agent.setLastValidScanningSession(this.$state._patient.currentSessionID);
				}
				const response = await agent.startScan(this.scanType);
				if (response.error) {
					console.error(response.error);
					this.scanState = ScanState.SCANNING_ERROR;
					return;
				}
				this.$state.biometric.lastValidScanningSessionID = this.$state._patient.currentSessionID;

				this.$state._patient.markAsDirty();
				this.$state.biometric.clear(this.scanType);
				this.$state.biometric.areScansUpToDate = false;
				// make sure metadata is restarted
				this.$state.leroy.leroyManager.metadata = undefined;

				const lastValidSession = this.$state._patient.patient?.fitstationProfile?.data?.lastValidScanningSession;
				if (!lastValidSession) {
					console.error('Cannot scan without fitstation profile');
					return;
				}
				await this.$state.leroy.restart(true);
				if (lastValidSession !== this.$state._patient.currentSessionID && hadScansBefore) {
					this.$state.agent.oneFootScan = false;
					this.$state.biometric.lastValidScanningSessionID = this.$state._patient.currentSessionID;
					this.$emit('clearScan', this.scanType === 'leftscan' ? 'right' : 'left');
					await this.$state.biometric.clear('both');
					this.$state.utils.pushMessage(errorMessages.snackbars.rescanNeeded);
				}
				await this.$state._patient.updateMedicalProfile({} as optionalUpdateData);
			} catch (e) {
				this.scanState = ScanState.SCANNING_ERROR;
				this.$emit('scanError', 'Cannot start scan.');
			}
		}

		private notFound() {
			this.scanState = ScanState.NOT_FOUND;
		}

		public async agentMessageHandler(e: any) {
			const messageData = e.data;

			if (messageData.event === '3Drightfoot' && this.scanType === 'rightscan') {
				await this.handleScan(messageData.data);
			}

			if (messageData.event === '3Dleftfoot' && this.scanType === 'leftscan') {
				await this.handleScan(messageData.data);
			}
		}

		public async handleScan(data: any) {
			if (data === 'finished') {
				if (this.scanState === ScanState.SCANNING) {
					this.scanState = ScanState.RENDERING;
					return;
				}
				this.scanState = ScanState.RENDERED;
			}

			if (data === 'failed') {
				this.scanState = ScanState.SCANNING_ERROR;
			}
		}
	}

	export enum ScanState {
		CHECKING, // ----> checking if there are scan, both in agent and platform
		NOT_FOUND, // ----> scans not found, need to display scanning instructoins
		SCANNING, // ----> scanner is scanning
		SCANNING_ERROR, // ----> scanner error while scanning
		RENDERING, // ----> scan found or scanned, now Three should start rendering the obj
		RENDERED // ----> scan rendered
	}
